import React, { useEffect, useState } from 'react';
import axios from "axios";
import { api_url } from "../Hooks/Global";
import Swal from 'sweetalert2';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

function StudentEnrollments() {
    const { student_pnr } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [srn, setSrn] = useState('');
    const [loading, setLoading] = useState(false);
    const [enrollments, setEnrollments] = useState([]);
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const querySrn = queryParams.get("srn");

        const finalSRN = student_pnr || querySrn;

        if (finalSRN) {
            setSrn(finalSRN);
            handleFetchEnrollments(finalSRN);
        }
    }, [student_pnr, location.search]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 0:
                return <span className="badge bg-warning text-dark">Pending Enrollment</span>;
            case 1:
                return <span className="badge bg-info text-white">Enrolled</span>;
            case 3:
                return <span className="badge bg-success text-white">Passed</span>;
            default:
                return <span className="badge bg-secondary">Unknown</span>;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 0:
                return 'border-warning';
            case 1:
                return 'border-info';
            case 3:
                return 'border-success';
            default:
                return 'border-secondary';
        }
    };

    const handleFetchEnrollments = async (srnValue = null) => {
        const enrollmentSRN = srnValue || srn;

        if (!enrollmentSRN || enrollmentSRN.trim() === '') {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Input',
                text: 'Please enter a valid SRN (Student Registration Number)',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(`${api_url}Masters/GetStudentsProgramWithSrn`, {
                params: { student_pnr: enrollmentSRN }
            });

            const { success, data, message } = response.data;

            if (success && data && data.length > 0) {
                setEnrollments(data);
                setIsDataFetched(true);
                setResponseMessage(`Found ${data.length} program(s) for SRN: ${enrollmentSRN}`);
            } else {
                setEnrollments([]);
                setIsDataFetched(false);
                setResponseMessage(message || 'No programs found for this SRN');
                Swal.fire({
                    icon: 'error',
                    title: 'No Data Found',
                    text: message || 'No programs found for this SRN',
                    confirmButtonColor: '#3085d6'
                });
            }
        } catch (error) {
            console.error("Error fetching student enrollments:", error);
            setEnrollments([]);
            setIsDataFetched(false);
            setResponseMessage(error.response?.data?.message || 'Failed to fetch enrollments. Please try again later.');
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to fetch enrollments. Please try again later.',
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleFetchEnrollments();
    };

    const handleInputChange = (e) => {
        setSrn(e.target.value);
        if (isDataFetched) {
            setIsDataFetched(false);
            setEnrollments([]);
        }
        setResponseMessage('');
    };

    const handleReset = () => {
        setSrn('');
        setIsDataFetched(false);
        setEnrollments([]);
        setResponseMessage('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <>
            <div className="inner-page-banner-area" style={{ backgroundImage: 'url("/images/About_images/about_banner.jpg")' }}>
                <div className="container">
                    <div className="pagination-area">
                        <h1>Student Enrollments</h1>
                    </div>
                </div>
            </div>

            <div className='container'>
                <div className='row d-flex justify-content-center'>
                    <div className='col-md-6'>
                        <div className='card rounded rounded-5 text-light shadow my-4 p-4 bg-primary'>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label text-light fs-6 fw-bold">Enter SRN (Student Registration Number)</label>
                                    <div className='d-flex'>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder='Enter SRN here...'
                                            value={srn}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        <button
                                            type="submit"
                                            className="btn btn-warning ms-2"
                                            disabled={loading}
                                        >
                                            {loading ? 'Searching...' : 'Search'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary ms-2"
                                            onClick={handleReset}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {isDataFetched && enrollments.length > 0 && (
                    <div className='row mb-5'>
                        <div className='col-12'>
                            <h3 className='mb-4 text-center text-dark fw-bold'>
                                <i className="fa fa-graduation-cap me-2"></i>
                                Enrolled Programs ({enrollments.length})
                            </h3>
                            <div className='row g-4'>
                                {enrollments.map((enrollment, index) => {

                                    const status = enrollment.students_programs_status;

                                    const statusConfig = {
                                        0: {
                                            label: 'Pending Enrollment',
                                            color: '#f59e0b',
                                            bg: '#fffbeb',
                                            icon: 'fa-clock-o'
                                        },
                                        1: {
                                            label: 'Enrolled',
                                            color: '#dc2626',
                                            bg: '#fef2f2',
                                            icon: 'fa-check-circle'
                                        },
                                        3: {
                                            label: 'Passed',
                                            color: '#16a34a',
                                            bg: '#f0fdf4',
                                            icon: 'fa-graduation-cap'
                                        }
                                    };

                                    const currentStatus = statusConfig[status] || {
                                        label: 'Unknown',
                                        color: '#64748b',
                                        bg: '#f8fafc',
                                        icon: 'fa-question-circle'
                                    };

                                    return (
                                        <div
                                            className='col-md-6 col-xl-4'
                                            key={enrollment.student_program_id || index}
                                        >
                                            <div
                                                className='card enrollment-card h-100 border-0 overflow-hidden'
                                                style={{
                                                    borderRadius: '18px',
                                                    background: currentStatus.bg,
                                                    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >

                                                {/* Status Top Line */}
                                                <div
                                                    style={{
                                                        height: '5px',
                                                        background: currentStatus.color
                                                    }}
                                                ></div>

                                                <div className='card-body p-4'>

                                                    {/* Header */}
                                                    <div className='d-flex justify-content-between align-items-start mb-4'>

                                                        <div className='d-flex align-items-center'>

                                                            <div
                                                                className='d-flex align-items-center justify-content-center me-3 flex-shrink-0'
                                                                style={{
                                                                    width: '48px',
                                                                    height: '48px',
                                                                    borderRadius: '13px',
                                                                    background: '#f1f5f9',
                                                                    color: '#334155'
                                                                }}
                                                            >
                                                                <i className='fa fa-book fa-lg'></i>
                                                            </div>

                                                            <div>
                                                                <div
                                                                    className='text-muted'
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        fontWeight: '700',
                                                                        letterSpacing: '0.8px',
                                                                        textTransform: 'uppercase'
                                                                    }}
                                                                >
                                                                    Program
                                                                </div>

                                                                <h5
                                                                    className='mb-0 mt-1 fw-bold text-dark'
                                                                    style={{
                                                                        lineHeight: '1.3'
                                                                    }}
                                                                >
                                                                    {enrollment.program_short_title ||
                                                                        enrollment.program_title ||
                                                                        'Program'}
                                                                </h5>
                                                            </div>

                                                        </div>

                                                        {/* Status */}
                                                        <span
                                                            className='d-inline-flex align-items-center flex-shrink-0'
                                                            style={{
                                                                color: currentStatus.color,
                                                                background: currentStatus.bg,
                                                                borderRadius: '30px',
                                                                padding: '7px 11px',
                                                                fontSize: '14px',
                                                                fontWeight: '700',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            <i
                                                                className={`fa ${currentStatus.icon} me-1`}
                                                            ></i>

                                                            {currentStatus.label}
                                                        </span>

                                                    </div>


                                                    {/* Program Title */}
                                                    <div className='mb-4'>

                                                        <div
                                                            className='text-muted mb-1'
                                                            style={{
                                                                fontSize: '10px',
                                                                fontWeight: '700',
                                                                letterSpacing: '0.6px',
                                                                textTransform: 'uppercase'
                                                            }}
                                                        >
                                                            Program Title
                                                        </div>

                                                        <div
                                                            className='fw-semibold text-dark'
                                                            style={{
                                                                fontSize: '15px',
                                                                lineHeight: '1.5'
                                                            }}
                                                        >
                                                            {enrollment.program_title || 'N/A'}
                                                        </div>

                                                    </div>


                                                    {/* Program Information */}
                                                    <div
                                                        className='p-3 mb-4'
                                                        style={{
                                                            background: '#f8fafc',
                                                            borderRadius: '14px'
                                                        }}
                                                    >

                                                        <div className='row g-3'>

                                                            {/* Duration */}
                                                            <div className='col-6'>

                                                                <div
                                                                    className='text-muted mb-1'
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        fontWeight: '700',
                                                                        letterSpacing: '0.5px'
                                                                    }}
                                                                >
                                                                    DURATION
                                                                </div>

                                                                <div className='d-flex align-items-center'>

                                                                    <i
                                                                        className='fa fa-calendar text-primary me-2'
                                                                        style={{
                                                                            fontSize: '14px'
                                                                        }}
                                                                    ></i>

                                                                    <span
                                                                        className='fw-semibold text-dark'
                                                                        style={{
                                                                            fontSize: '13px'
                                                                        }}
                                                                    >
                                                                        {enrollment.program_duration
                                                                            ? `${enrollment.program_duration} Days`
                                                                            : 'N/A'}
                                                                    </span>

                                                                </div>

                                                            </div>


                                                            {/* Enrollment Number */}
                                                            <div className='col-6'>

                                                                <div
                                                                    className='text-muted mb-1'
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        fontWeight: '700',
                                                                        letterSpacing: '0.5px'
                                                                    }}
                                                                >
                                                                    ENROLLMENT NO.
                                                                </div>

                                                                <div className='d-flex align-items-center'>

                                                                    <i
                                                                        className='fa fa-hashtag text-primary me-2'
                                                                        style={{
                                                                            fontSize: '14px'
                                                                        }}
                                                                    ></i>

                                                                    <span
                                                                        className='fw-semibold text-dark text-truncate'
                                                                        style={{
                                                                            fontSize: '13px'
                                                                        }}
                                                                        title={
                                                                            enrollment.enrollment_number ||
                                                                            'N/A'
                                                                        }
                                                                    >
                                                                        {enrollment.enrollment_number ||
                                                                            'N/A'}
                                                                    </span>

                                                                </div>

                                                            </div>

                                                        </div>

                                                    </div>


                                                    {/* Franchise */}
                                                    <div className='d-flex align-items-center'>

                                                        <div
                                                            className='d-flex align-items-center justify-content-center me-3 flex-shrink-0'
                                                            style={{
                                                                width: '42px',
                                                                height: '42px',
                                                                borderRadius: '11px',
                                                                background: '#fff7ed',
                                                                color: '#ea580c'
                                                            }}
                                                        >
                                                            <i className='fa fa-building'></i>
                                                        </div>

                                                        <div className='overflow-hidden'>

                                                            <div
                                                                className='text-muted'
                                                                style={{
                                                                    fontSize: '10px',
                                                                    fontWeight: '700',
                                                                    letterSpacing: '0.6px'
                                                                }}
                                                            >
                                                                FRANCHISE
                                                            </div>

                                                            <div
                                                                className='fw-semibold text-dark text-truncate'
                                                                style={{
                                                                    fontSize: '14px'
                                                                }}
                                                                title={
                                                                    enrollment.franchise_name || 'N/A'
                                                                }
                                                            >
                                                                {enrollment.franchise_name || 'N/A'}
                                                            </div>

                                                            {enrollment.franchise_city && (
                                                                <div
                                                                    className='text-muted'
                                                                    style={{
                                                                        fontSize: '12px'
                                                                    }}
                                                                >
                                                                    <i className='fa fa-map-marker me-1'></i>
                                                                    {enrollment.franchise_city}
                                                                </div>
                                                            )}

                                                        </div>

                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {isDataFetched && enrollments.length === 0 && (
                    <div className='row d-flex justify-content-center mb-5'>
                        <div className='col-md-8'>
                            <div className='card shadow bg-light'>
                                <div className='card-body text-center py-5'>
                                    <i className="fa fa-search fa-3x text-muted mb-3"></i>
                                    <h4 className='text-muted'>No enrollments found</h4>
                                    <p className='text-muted'>This SRN doesn't have any enrolled programs yet.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>
                {`
                .enrollment-card {
    cursor: default;
}

.enrollment-card:hover {
    transform: translateY(-7px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12) !important;
}
    `}
            </style>
        </>
    );
}

export default StudentEnrollments;